import type { Population } from "src/graphql/generated";

/**
 * Which partner organizations may be assigned to a new engagement.
 *
 * Archived means retired: we have stopped working with the firm, so nothing new is
 * recorded through it. The row is otherwise untouched — every existing engagement
 * keeps its reference, and history keeps resolving the id to the name.
 *
 * **This is presentation, not enforcement.** The rule is enforced in the store, by
 * `refuseArchivedOrganization` inside the engagement insert's transaction, and it has
 * to be: `useLookups` caches for ten minutes, so a form left open can hold a firm
 * archived since it loaded, and a direct API call never reaches this code at all.
 * What this function does is keep a retired firm from being offered, so the ordinary
 * path never produces the error rather than relying on it.
 *
 * The filtering lives here rather than in the query on purpose. `useLookups` feeds
 * both these pickers and the history panel's id-to-name resolution, and the backend
 * comment on `loadOrganizations` says why it must keep returning archived rows: an
 * archived firm's past terms would otherwise render as "an organization that no
 * longer exists" for a row that plainly does exist. So the server returns
 * everything, and the one reader that needs a shorter list narrows it here.
 *
 * No "except the currently selected one" case, and none is needed *here*: both
 * callers are create forms, so a selected value is always one this list just
 * offered. The edit form is the case this could not serve, and it has its own
 * function below — which is what this comment used to predict.
 */
export function assignableOrganizations<T extends { archivedAt?: string | null }>(
  orgs: readonly T[] | undefined
): T[] {
  return (orgs ?? []).filter((org) => !org.archivedAt);
}

/**
 * Which organizations an *edit* form may offer: the assignable ones, plus the firm
 * the term already runs through even when that firm has been archived.
 *
 * The extra case is not a nicety, it is a data-loss guard, and
 * `assignableOrganizations`' own comment called it before this feature existed. A
 * `<Select>` whose value is absent from its options renders with nothing selected.
 * So a term through a firm archived since would show an empty picker, and an
 * operator who came to fix the title would submit a cleared organization — silently
 * dropping the firm from a term nobody asked to change.
 *
 * Keeping it is also the correct rule rather than a workaround: archiving retires a
 * firm from *new* assignment and deliberately leaves existing engagements untouched,
 * which the store enforces by checking only assignments that actually change the
 * value. The picker and the store therefore agree — a term may keep the retired firm
 * it has, and may not be moved onto one.
 *
 * `currentId` of null or undefined is the independent-individual case and adds
 * nothing, so this is just `assignableOrganizations` then.
 */
export function organizationsForEditing<T extends { id: string; archivedAt?: string | null }>(
  orgs: readonly T[] | undefined,
  currentId: string | null | undefined
): T[] {
  const assignable = assignableOrganizations(orgs);
  if (!currentId || assignable.some((org) => org.id === currentId)) {
    return assignable;
  }
  const current = (orgs ?? []).find((org) => org.id === currentId);
  if (!current) {
    // The term names a firm this console cannot see — a lookup list ten minutes
    // stale, or one that failed to load. Left out rather than invented: the caller
    // renders the id it holds, and offering a fabricated option would be worse than
    // an honest gap. Same restraint as INFRA-651's unresolvable-id fallback.
    return assignable;
  }
  // First, because it is the value the field already has, and a picker that buries
  // its own current value below the alternatives reads as though it were unset.
  return [current, ...assignable];
}

/**
 * The firm a term runs through, for the row that shows it rather than the form that
 * writes it.
 *
 * The gap this closes: `partnerOrganization` was read by the edit form and by nothing
 * else, so the only way to learn which firm somebody is engaged through was to open a
 * form that writes — which a partner *reader* is never offered. The firm was therefore
 * unreadable to exactly the person most likely to be asking.
 *
 * Three answers, not two, because the two absences are different facts:
 *
 *   - An on-demand contractor gets `null`. The store refuses a firm on that population
 *     outright (`TestODCEngagementRefusesAPartnerOrganization`), so the row has nothing
 *     to say on the subject — and says nothing, exactly as the edit form omits the
 *     field rather than offering an empty one.
 *   - A partner with no firm is an independent individual. That is a settled fact about
 *     the term rather than a blank, so it is named — in the edit form's own words
 *     ("blank for independent individuals"), so the read view and the write view
 *     describe one arrangement with one phrase.
 *
 * Named rather than dashed, both times. The dash in this console means *not recorded*:
 * the organizations roster reserves it for a headcount nobody took, and the People list
 * shows a bare country code rather than a dash for the same reason. Neither of these
 * absences is that, and a dash here would send an operator looking for a firm that does
 * not exist.
 *
 * Lower case and phrase-shaped, both times, because this renders under the population
 * rather than under a header of its own — "through Smith, Jones & Co" and "independent
 * individual" each finish the sentence the cell above them starts.
 */
export function describeEngagementFirm(engagement: {
  population: Population;
  partnerOrganization?: { name: string } | null;
}): string | null {
  if (engagement.population === "ON_DEMAND_CONTRACTOR") {
    return null;
  }
  return engagement.partnerOrganization
    ? `through ${engagement.partnerOrganization.name}`
    : "independent individual";
}
